import { type ReactNode, useEffect, useRef } from 'react';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { ErrorBoundary as ErrorBoundary_ } from '@gonfalon/error-boundaries/ErrorBoundary';
import { isFlagError } from '@gonfalon/flags/errors';
import { isRESTAPIError } from '@gonfalon/rest-api/errors';
import { Alert, Button } from '@launchpad-ui/components';
import nullthrows from 'nullthrows';

import { clearAiMonitoringStorage } from '#components/AiMonitoring/util/clearAiMonitoringStorage';
import { useCurrentEnvironmentKey } from '#reducers/projects';

export function SyncReduxWithProjectContext({ children }: { children: ReactNode }) {
  const context = useProjectContext();
  const legacyCurrentEnvironmentKey = useCurrentEnvironmentKey();
  const previousProjectKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      context: { projectKey, selectedEnvironmentKey },
      environments,
      project: project_,
    } = context;

    const currentEnvironment = environments.find((env) => env.key === selectedEnvironmentKey);

    // Clear AI Monitoring storage only when project changes (not environment changes)
    if (previousProjectKeyRef.current !== null && previousProjectKeyRef.current !== projectKey) {
      clearAiMonitoringStorage();
    }
    previousProjectKeyRef.current = projectKey;

    window.__resetStore({
      condition: ({ currentProjectKey, currentEnvironmentKey }) =>
        currentProjectKey !== projectKey || currentEnvironmentKey !== selectedEnvironmentKey,
      payload: {
        initialState: {
          currentProject: project_,
          currentEnvironment: nullthrows(currentEnvironment, 'Expected current environment'),
        },
      },
    });
  }, [context]);

  if (legacyCurrentEnvironmentKey !== context.context.selectedEnvironmentKey) {
    return null;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function ErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary_ fallbackRender={Fallback} severity="high" ignoredStatuses={[404]}>
      {children}
    </ErrorBoundary_>
  );
}

function Fallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  if (isFlagError(error)) {
    switch (error.code) {
      case 'missing_environment':
        return (
          <Alert variant="inline" status="error">
            {error.message}
            <Button size="small" onPress={() => resetErrorBoundary()}>
              Try again
            </Button>
          </Alert>
        );
      default:
        return <div>Something went wrong</div>;
    }
  }
  if (isRESTAPIError(error) && error.status === 404) {
    return <div>Flag not found. Please refresh the page.</div>;
  }
  return <div>Something went wrong</div>;
}
